import { ACCOUNT_METADATA } from '@/lib/auth/metadata';
import { BRAND } from '@/lib/cms/config';

const DEFAULT_CAMPAIGN_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
const REGEXP_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

function getDefaultCampaignCookieName(): string {
  return `${BRAND}_campaign`;
}

function escapeRegExp(value: string): string {
  return value.replaceAll(REGEXP_SPECIAL_CHARS, String.raw`\$&`);
}
const UTM_VALUE_MAX_LENGTH = 255;

export interface CampaignData {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
}

/** MyAccount / POS Manager REST payload fields (Postman UTM Epic). */
export interface UtmApiFields {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
}

function normalizeUtmValue(value: string): string {
  return value.trim().slice(0, UTM_VALUE_MAX_LENGTH);
}

export function getCampaignCookieName(): string {
  return process.env.NEXT_PUBLIC_CAMPAIGN_COOKIE_NAME || getDefaultCampaignCookieName();
}

export function getCampaignCookieMaxAge(): number {
  const raw = process.env.NEXT_PUBLIC_CAMPAIGN_COOKIE_MAX_AGE;
  if (!raw) return DEFAULT_CAMPAIGN_COOKIE_MAX_AGE_SECONDS;

  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_CAMPAIGN_COOKIE_MAX_AGE_SECONDS;
}

export function getCampaignCookieDomain(): string | undefined {
  const raw = process.env.NEXT_PUBLIC_CAMPAIGN_COOKIE_DOMAIN?.trim();
  if (!raw) return undefined;
  return raw.startsWith('.') ? raw : `.${raw}`;
}

export function getCampaignDataFromSearchParams(
  searchParams: URLSearchParams
): CampaignData | null {
  const campaign = searchParams.get('utm_campaign');
  if (!campaign?.trim()) {
    return null;
  }

  return {
    utm_source: normalizeUtmValue(searchParams.get('utm_source') || ''),
    utm_medium: normalizeUtmValue(searchParams.get('utm_medium') || ''),
    utm_campaign: normalizeUtmValue(campaign),
  };
}

export function serializeCampaignData(data: CampaignData): string {
  return encodeURIComponent(JSON.stringify(data));
}

export function parseCampaignCookieValue(rawValue: string | undefined): CampaignData | null {
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(decodeURIComponent(rawValue));
    if (!parsed || typeof parsed !== 'object') return null;

    const utmSource =
      typeof parsed.utm_source === 'string' ? normalizeUtmValue(parsed.utm_source) : '';
    const utmMedium =
      typeof parsed.utm_medium === 'string' ? normalizeUtmValue(parsed.utm_medium) : '';
    const utmCampaign =
      typeof parsed.utm_campaign === 'string' ? normalizeUtmValue(parsed.utm_campaign) : '';

    if (!utmCampaign) return null;

    return {
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    };
  } catch {
    return null;
  }
}

export function readCampaignCookieFromDocument(): CampaignData | null {
  if (globalThis.document === undefined) return null;

  const cookieName = getCampaignCookieName();
  const escapedName = escapeRegExp(cookieName);
  const pattern = new RegExp(String.raw`(?:^|;\s*)${escapedName}=([^;]*)`);
  const match = pattern.exec(globalThis.document.cookie);
  return parseCampaignCookieValue(match?.[1]);
}

function campaignDataEquals(a: CampaignData, b: CampaignData): boolean {
  return (
    a.utm_source === b.utm_source &&
    a.utm_medium === b.utm_medium &&
    a.utm_campaign === b.utm_campaign
  );
}

/**
 * Client-side write mirroring proxy `applyCampaignCookieFromUrl` options.
 * Used on /store/* where proxy skips Set-Cookie to avoid ERR_HTTP_HEADERS_SENT.
 */
export function writeCampaignCookieToDocument(data: CampaignData): void {
  if (globalThis.document === undefined) return;

  const cookieName = getCampaignCookieName();
  const domain = getCampaignCookieDomain();
  const isSecure = globalThis.location?.protocol === 'https:';

  const parts = [
    `${cookieName}=${serializeCampaignData(data)}`,
    'path=/',
    `max-age=${getCampaignCookieMaxAge()}`,
    'SameSite=Lax',
  ];
  if (domain) parts.push(`domain=${domain}`);
  if (isSecure) parts.push('Secure');

  globalThis.document.cookie = parts.join('; ');
}

/**
 * Persist UTM params from the URL into the campaign cookie when present and
 * different from the existing cookie. Returns true when a write occurred.
 */
export function persistCampaignFromUrlSearch(search: string | URLSearchParams): boolean {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const fromUrl = getCampaignDataFromSearchParams(params);
  if (!fromUrl) return false;

  const existing = readCampaignCookieFromDocument();
  if (existing && campaignDataEquals(existing, fromUrl)) return false;

  writeCampaignCookieToDocument(fromUrl);
  return true;
}

/** Saleor user metadata on account create/register (`user.utm_*` — Breitling parity). */
export function toUtmMetadataInput(data: CampaignData): Array<{ key: string; value: string }> {
  return [
    { key: ACCOUNT_METADATA.utmSource, value: data.utm_source },
    { key: ACCOUNT_METADATA.utmMedium, value: data.utm_medium },
    { key: ACCOUNT_METADATA.utmCampaign, value: data.utm_campaign },
  ];
}

/** Checkout metadata before orderCreate (`client.utm_*` — Breitling parity / BEC-20520). */
export function toUtmCheckoutMetadataInput(
  data: CampaignData
): Array<{ key: string; value: string }> {
  return [
    { key: ACCOUNT_METADATA.checkoutUtmSource, value: data.utm_source },
    { key: ACCOUNT_METADATA.checkoutUtmMedium, value: data.utm_medium },
    { key: ACCOUNT_METADATA.checkoutUtmCampaign, value: data.utm_campaign },
  ];
}

/** REST payload fields — omit when cookie is absent (AC9). */
export function toUtmApiFields(
  campaignCookie: CampaignData | null | undefined
): Partial<UtmApiFields> {
  if (!campaignCookie) return {};

  return {
    utmSource: campaignCookie.utm_source,
    utmMedium: campaignCookie.utm_medium,
    utmCampaign: campaignCookie.utm_campaign,
  };
}
